import { useEffect, useRef } from 'react'
import { defaultRegionOf } from '@/data/productLines'
import { useInfraEnvironments, useInfraRegions, useInfraResourceGroups } from '@/hooks/useInfra'
import { lookupErrorMessage } from '@/services/infraLive'
import type { InfraEnvironment, InfraProduct, InfraRegion, InfraResourceGroup } from '@/types/infrastructure'
import page from '@/pages/NewDeploymentPage.module.css'
import { LookupSelect } from './LookupSelect'

interface CascadingLocationSelectProps {
  product: InfraProduct
  environment: InfraEnvironment | null
  region: InfraRegion | null
  resourceGroup: InfraResourceGroup | null
  onEnvironmentChange: (environment: InfraEnvironment | null) => void
  onRegionChange: (region: InfraRegion | null) => void
  onResourceGroupChange: (group: InfraResourceGroup | null) => void
  idPrefix?: string
}

/**
 * Environment -> Region -> Resource group, strictly in that order.
 *
 * Each select is disabled until its parent is chosen. Choosing a different parent calls the parent's handler, and the owner
 * (the store) clears everything beneath it; the children's queries are keyed by their parents, so they refetch by themselves.
 * What each level offers comes from infraService and depends on the product.
 */
export function CascadingLocationSelect({
  product,
  environment,
  region,
  resourceGroup,
  onEnvironmentChange,
  onRegionChange,
  onResourceGroupChange,
  idPrefix = 'location',
}: CascadingLocationSelectProps) {
  const environments = useInfraEnvironments(product)
  const regions = useInfraRegions(product, environment?.id ?? '')
  const groups = useInfraResourceGroups(product, environment?.id ?? '', region?.id ?? '')

  // Most customers of a product line are in one region (Traverse: North Central US), so it is chosen as soon as the environment's regions
  // are known, when the environment offers it and it is ready. Once per product line and environment: a region the user then changes
  // (or clears) is theirs, and is not chosen again behind their back.
  const usualRegion = defaultRegionOf(product)
  const chosenFor = useRef('')
  useEffect(() => {
    if (!usualRegion || region || !environment || !regions.data) return
    const key = `${product.line ?? product.slug}|${environment.id}`
    if (chosenFor.current === key) return
    chosenFor.current = key
    const usual = regions.data.find((item) => item.id === usualRegion && item.issues.length === 0)
    if (usual) onRegionChange(usual)
  }, [usualRegion, region, environment, regions.data, product.line, product.slug, onRegionChange])

  return (
    <div className={page.formGrid}>
      <LookupSelect
        id={`${idPrefix}-environment`}
        label="Environment"
        required
        value={environment?.id ?? ''}
        options={(environments.data ?? []).map((item) => ({ value: item.id, label: item.label }))}
        onChange={(id) => onEnvironmentChange((environments.data ?? []).find((item) => item.id === id) ?? null)}
        isLoading={environments.isLoading}
        isError={environments.isError}
        onRetry={() => void environments.refetch()}
        emptyMessage={`No environments available for ${product.name}.`}
      />
      <LookupSelect
        id={`${idPrefix}-region`}
        label="Region"
        required
        value={region?.id ?? ''}
        options={(regions.data ?? []).map((item) => ({
          value: item.id,
          label: item.issues.length > 0 ? `${item.label} (not ready)` : item.label,
        }))}
        onChange={(id) => onRegionChange((regions.data ?? []).find((item) => item.id === id) ?? null)}
        waitingFor={environment ? undefined : 'an environment'}
        isLoading={regions.isLoading}
        isError={regions.isError}
        errorMessage={lookupErrorMessage(regions.error)}
        onRetry={() => void regions.refetch()}
        emptyMessage="No regions available for this environment."
        hint={
          region && region.id === usualRegion
            ? `${region.label} is where most ${product.name} customers are, so it is chosen for you. Change it if this customer is elsewhere.`
            : `Regions where ${product.name} can be placed in ${environment?.label ?? 'this environment'}.`
        }
      />
      <LookupSelect
        id={`${idPrefix}-resource-group`}
        label="Resource group"
        required
        value={resourceGroup?.id ?? ''}
        options={(groups.data ?? []).map((item) => ({ value: item.id, label: item.name }))}
        onChange={(id) => onResourceGroupChange((groups.data ?? []).find((item) => item.id === id) ?? null)}
        waitingFor={region ? undefined : 'a region'}
        isLoading={groups.isLoading}
        isError={groups.isError}
        errorMessage={lookupErrorMessage(groups.error)}
        onRetry={() => void groups.refetch()}
        emptyMessage="No resource groups available in this region."
      />
    </div>
  )
}
